<template>
  <v-app-bar id="top-bar" v-if="user && !tempUser" scroll-target="#app-content" scroll-behavior="elevate" height="76">
    <v-app-bar-nav-icon v-if="!drawer" @click="toggleMenu()" />

    <v-spacer></v-spacer>

    <v-menu v-if="isAdmin && $route.name === 'home'" open-on-hover :close-on-content-click="false" location="bottom">
      <template v-slot:activator="{ props }">
        <v-btn variant="text" v-bind="props" class="me-1">
          <v-icon :color="hasViewAsOverride ? 'primary' : undefined">mdi-eye</v-icon>
        </v-btn>
      </template>
      <v-card class="view-as-popup pa-4" min-width="260">
        <div class="text-body-2 font-weight-medium mb-3">View As</div>
        <div class="text-caption text-medium-emphasis mb-1">Current program: {{ realProgramType }}</div>
        <div class="text-caption text-medium-emphasis mb-3">Current onboarding: {{ realOnboarded ? 'Onboarded' : 'Not onboarded' }}</div>
        <v-select
          :model-value="adminViewAs.programType"
          @update:model-value="setViewAsProgramType"
          :items="programTypeOptions"
          label="Program type"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-3"
        />
        <v-select
          :model-value="adminViewAs.onboarded"
          @update:model-value="setViewAsOnboarded"
          :items="onboardingOptions"
          label="Onboarding status"
          density="compact"
          variant="outlined"
          hide-details
          class="mb-3"
        />
        <v-select
          :model-value="adminViewAs.hidden"
          @update:model-value="setViewAsHidden"
          :items="hiddenOptions"
          label="Hidden"
          density="compact"
          variant="outlined"
          hide-details
        />
      </v-card>
    </v-menu>

    <v-menu v-if="!isCoursePage">
      <template v-slot:activator="{ props }">
        <v-btn variant="text" v-bind="props">
          <v-img v-if="$i18n.locale === 'en'" src="/language/en.png" class="me-2" width="24" />
          <v-img v-if="$i18n.locale === 'fi'" src="/language/fi.png" class="me-2" width="24" />
          <v-img v-if="$i18n.locale === 'sv'" src="/language/sv.png" class="me-2" width="24" />
          <v-img v-if="$i18n.locale === 'fr'" src="/language/fr.png" class="me-2" width="24" />
          <v-img v-if="$i18n.locale === 'de'" src="/language/de.png" class="me-2" width="24" />
          <v-img v-if="$i18n.locale === 'es'" src="/language/es.png" class="me-2" width="24" />
          <v-icon> mdi-chevron-down </v-icon>
        </v-btn>
      </template>

      <v-list>
        <v-list-item v-for="lang in localesOptions" :key="lang.value" @click="changeLocale(lang.value)">
          <template v-slot:prepend class="mr-5">
            <v-img v-if="lang.value === 'en'" src="/language/en.png" class="me-2" width="24" />
            <v-img v-if="lang.value === 'fi'" src="/language/fi.png" class="me-2" width="24" />
            <v-img v-if="lang.value === 'sv'" src="/language/sv.png" class="me-2" width="24" />
            <v-img v-if="lang.value === 'fr'" src="/language/fr.png" class="me-2" width="24" />
            <v-img v-if="lang.value === 'de'" src="/language/de.png" class="me-2" width="24" />
            <v-img v-if="lang.value === 'es'" src="/language/es.png" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ lang.text }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-btn variant="text" class="me-2" @click="inboxOpened = true">
      <v-badge dot color="#74bb6c" overlap v-if="showNotificationBadge">
        <v-icon> mdi-bell </v-icon>
      </v-badge>
      <v-icon v-else> mdi-bell </v-icon>
    </v-btn>

    <v-menu location="bottom" position="absolute" :position-y="80" :position-x="$vuetify.display.width - 25">
      <template v-slot:activator="{ props }">
        <div v-bind="props" class="d-flex align-center">
          <user-bubble data-hj-suppress class="bubble" id="user-bubble" :user="user" :size="35" />
          <div class="d-flex flex-column justify-center align-start mx-3">
            <div data-hj-suppress class="username data-hj-suppress">{{ user.name }}</div>
            <div class="role">{{ user.role }}</div>
          </div>
          <v-icon> mdi-chevron-down </v-icon>
        </div>
      </template>
      <v-list>
        <v-list-item v-if="can('view', 'MyRecordings')" @click="navigateTo('my-recordings')">
          <template v-slot:prepend>
            <v-img src="/icons/my-recordings.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_recordings") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="can('view', 'Program')" @click="navigateTo('programs')">
          <template v-slot:prepend>
            <v-img src="/icons/my-programs.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_programs") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="can('create', 'Course')" @click="navigateTo('my-courses')">
          <template v-slot:prepend>
            <v-img src="/icons/ic-course.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_courses") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="can('view', 'MyAvailability')" @click="navigateTo('coach-calendar')">
          <template v-slot:prepend>
            <v-img src="/icons/ic-calendar.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_availability") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="can('view', 'MySessions')" @click="navigateTo('sessions')">
          <template v-slot:prepend>
            <v-img src="/icons/my-sessions.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_sessions") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item @click="navigateTo('profile')">
          <template v-slot:prepend>
            <v-img src="/icons/ic-settings.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.my_profile") }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="user.role === 'Administrator'" @click="navigateTo('editor')">
          <template v-slot:prepend>
            <v-img src="/icons/editor.svg" class="me-2" width="24" />
          </template>
          <v-list-item-title>
            {{ $t("common.navigation.editor") }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <notification-inbox v-if="inboxOpened" @close="inboxOpened = false" />
    <v-bottom-sheet
      v-model="showChangeLocaleBottom"
      :persistent="true"
      :inset="true"
      max-width="700px"
      content-class="mx-auto"
    >
      <v-sheet class="text-center pa-3 rounded" height="230px">
        <div>
          <v-icon size="70" color="warning">mdi-web</v-icon>

          <h3 class="text-h3 mt-3">
            {{ $t("components.top_bar.change_locale", { locale: $i18n.locale.toUpperCase() }) }}
          </h3>
        </div>
        <v-btn class="mt-6 mb-6" variant="text" @click="updateUserLanguage()" color="error">
          {{ $t("common.actions.confirm") }}
        </v-btn>
        <v-btn class="mt-6 mb-6" variant="text" color="error" @click="showChangeLocaleBottom = !showChangeLocaleBottom">
          {{ $t("common.actions.cancel") }}
        </v-btn>
      </v-sheet>
    </v-bottom-sheet>
  </v-app-bar>
</template>
<script>
import permissions from "../mixins/permissions";
import NotificationInbox from "@/components/NotificationInbox.vue";
import UserBubble from "@/components/UserBubble.vue";
import locales from "@/mixins/locales";
import { loadLanguageAsync } from "@/translations";

export default {
  name: "TopBar",
  props: [],
  mixins: [permissions, locales],
  components: {
    UserBubble,
    NotificationInbox,
  },
  data() {
    return {
      showChangeLocaleBottom: false,
      inboxOpened: false,
      programTypeOptions: ["Free", "Lite", "Lite+", "Power Prep", "Executive"],
      onboardingOptions: [
        { title: "Onboarded", value: true },
        { title: "Not onboarded", value: false },
      ],
      hiddenOptions: [
        { title: "Show", value: true },
        { title: "Hide", value: false },
      ],
    };
  },
  computed: {
    drawer() {
      return this.$store.getters["userInterface/getDrawer"];
    },
    isCoursePage() {
      return ["courses", "courses/section", "courses/chapter"].includes(this.$route.name);
    },
    user() {
      return this.$store.getters["users/currentUser"];
    },
    tempUser() {
      return this.$store.getters["users/tempUser"];
    },
    showNotificationBadge() {
      const notifications = this.$store.getters["notifications/notifications"];
      if (!notifications) return false;
      return notifications.filter((n) => !n.seen).length > 0;
    },
    isAdmin() {
      return this.user?.role === "Administrator";
    },
    adminViewAs() {
      return this.$store.getters["userInterface/getAdminViewAs"];
    },
    hasViewAsOverride() {
      return (
        this.adminViewAs.programType !== null || this.adminViewAs.onboarded !== null || this.adminViewAs.hidden !== null
      );
    },
    realProgramType() {
      return this.$store.getters["programs/selectedProgram"]?.subscriptionType || "Free";
    },
    realOnboarded() {
      return this.$store.getters["onboarding/onboardingStatus"]?.onboardingComplete ?? null;
    },
  },
  methods: {
    toggleMenu() {
      this.$store.dispatch("userInterface/toggleDrawer");
    },
    changeLocale(lang) {
      this.showChangeLocaleBottom = this.user.language !== lang;

      loadLanguageAsync(lang);
    },
    navigateTo(route) {
      if (this.$router.currentRoute.name === route) {
        return;
      }

      this.$router.push({
        name: route,
      });
    },
    setViewAsProgramType(value) {
      this.$store.dispatch("userInterface/setAdminViewAs", { programType: value ?? null });
    },
    setViewAsOnboarded(value) {
      this.$store.dispatch("userInterface/setAdminViewAs", { onboarded: value ?? null });
    },
    setViewAsHidden(value) {
      this.$store.dispatch("userInterface/setAdminViewAs", { hidden: value ?? null });
    },
    updateUserLanguage() {
      this.$store.dispatch("users/saveUserProfile", {
        ...this.user,
        language: this.$i18n.locale,
      });

      this.showChangeLocaleBottom = false;
    },
  },
  mounted() {
    if (this.user && this.user.language && this.user.language !== this.$i18n.locale) {
      loadLanguageAsync(this.user.language);
    }
  },
};
</script>
<style lang="css" scoped>
#top-bar {
  background-color: var(--background-color);
  border-bottom: 1px solid var(--grey);
  display: block;
  position: sticky;
  top: 0;
  z-index: 5;
}

.username {
  color: #101319;
  font-family: Lora, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px; /* 157.143% */
}

.role {
  overflow: hidden;
  color: #101319;
  text-overflow: ellipsis;
  font-family: Lora, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 22px; /* 157.143% */
}

.bubble {
  float: left;
  margin-top: 7px;
  margin-left: 10px;
}

.view-as-popup {
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}
</style>
